import L, { LatLng, LatLngBounds, LeafletMouseEvent } from 'leaflet'
import React, { useCallback, useEffect, useRef } from 'react'
import { useMap } from 'react-leaflet'

export interface SelectablePoint {
  id: string
  lat: number
  lng: number
}

export interface SerializableLatLngBounds {
  northEast: L.LatLngLiteral
  southWest: L.LatLngLiteral
}

interface DragSelectProps {
  selectedMapArea: SerializableLatLngBounds | null
  setSelectedMapArea: (area: SerializableLatLngBounds | null) => void
}

/**
 * A component to render inside a Leaflet map to allow a user to highligh an area via alt-click.
 * @param props
 * @returns component
 */
const LeafletAreaSelectRaw: React.FC<DragSelectProps> = ({
  selectedMapArea,
  setSelectedMapArea,
}) => {
  const map = useMap()
  const rectangleRef = useRef<L.Rectangle | null>(null)
  const startPointRef = useRef<LatLng | null>(null)
  const isDraggingRef = useRef(false)

  useEffect(() => {
    if (selectedMapArea) {
      const bounds = new LatLngBounds(
        selectedMapArea.southWest,
        selectedMapArea.northEast
      )
      if (!rectangleRef.current) {
        rectangleRef.current = L.rectangle(bounds, {
          color: 'green',
          weight: 1,
        }).addTo(map)
      } else {
        rectangleRef.current.setBounds(bounds)
      }
    }
  }, [map, selectedMapArea])

  const onMouseDown = useCallback(
    (e: LeafletMouseEvent) => {
      if (!e.originalEvent.altKey) return // Only start if alt is pressed

      e.originalEvent?.preventDefault()
      e.originalEvent?.stopPropagation()

      isDraggingRef.current = true
      startPointRef.current = e.latlng

      map.dragging.disable() // Disable map dragging while selecting
    },
    [map]
  )

  const onMouseMove = useCallback(
    (e: LeafletMouseEvent) => {
      if (!isDraggingRef.current || !startPointRef.current) return

      const bounds = new LatLngBounds(startPointRef.current, e.latlng)

      if (!rectangleRef.current) {
        rectangleRef.current = L.rectangle(bounds, {
          color: 'green',
          weight: 1,
        }).addTo(map)
      } else {
        rectangleRef.current.setBounds(bounds)
      }
    },
    [map]
  )

  const onMouseUp = useCallback(
    (e: LeafletMouseEvent) => {
      if (!isDraggingRef.current || !startPointRef.current) return

      isDraggingRef.current = false
      map.dragging.enable() // Re-enable map dragging

      const bounds = new LatLngBounds(startPointRef.current, e.latlng)
      setSelectedMapArea({
        northEast: bounds.getNorthEast(),
        southWest: bounds.getSouthWest(),
      })

      startPointRef.current = null
    },
    [map, setSelectedMapArea]
  )

  const onClick = useCallback(
    (e: LeafletMouseEvent) => {
      if (e.originalEvent.altKey) return // Ignore alt-clicks

      // Clear selection on regular click
      if (rectangleRef.current) {
        map.removeLayer(rectangleRef.current)
        rectangleRef.current = null
      }
      setSelectedMapArea(null)
    },
    [map, setSelectedMapArea]
  )

  useEffect(() => {
    map.on('mousedown', onMouseDown)
    map.on('mousemove', onMouseMove)
    map.on('mouseup', onMouseUp)
    map.on('click', onClick)

    return () => {
      map.off('mousedown', onMouseDown)
      map.off('mousemove', onMouseMove)
      map.off('mouseup', onMouseUp)
      map.off('click', onClick)
    }
  }, [map, onMouseDown, onMouseMove, onMouseUp, onClick])

  return null
}

export const LeafletAreaSelect = React.memo(LeafletAreaSelectRaw)
